import React, { useEffect, useMemo, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getPhoneCode } from '../Scenes/Signin';
import { useSelector } from './index';
import { useConfig } from '../Components/ConfigProvider';
import { useShoppingCart, useShoppingCartApi } from '../Components/ShoppingCartUniverse';
import useI18n from '../i18n';

export type TContactData = {
  name: string;
  phone: string;
  errors: { name: string; phone: string };
};

const useCustomerData = () => {
  const { country, user } = useSelector((state) => state);
  const config = useConfig();
  const shoppingCart = useShoppingCart();
  const shoppingCartApi = useShoppingCartApi();
  const { i18n } = useI18n();

  const [contactData, setContactData] = useState<TContactData>({
    name: '',
    phone: '',
    errors: { name: '', phone: '' },
  });

  // Initializing the contact data
  useEffect(() => {
    if (!config.COLLECT_CUSTOMER_CONTACT_DATA) {
      return;
    }

    let data: Partial<TContactData | null> = null;

    // Add the default country code
    if (!user && country) {
      data = { phone: config.PHONE_COUNTRY_CODE ?? getPhoneCode(country) };
    }

    // Add info from User
    if (user) {
      const name = user.name;
      const phone = user.phone.slice(user.phone.indexOf('+'));

      data = { name, phone };
    }

    if (data) {
      setContactData((prevState) => ({ ...prevState, ...data }));
    }
  }, [user, country]);

  const onChangeContactData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactData((prev) => ({ ...prev, [name]: value, errors: { ...prev.errors, [name]: '' } }));
  };

  const getContactDataMatch = () => {
    const { comment } = shoppingCart;

    const title = i18n('Cart.CustomerData.Title');
    const regex = new RegExp(`\\s*${title}:`);
    return comment?.match(regex);
  };

  // Remove contact data from comment when returning to cart
  // If the user closed the modal for some reason, for example: payment error or cancellation
  const removeContactDataFromComment = () => {
    if (!config.COLLECT_CUSTOMER_CONTACT_DATA) {
      return;
    }

    const { comment } = shoppingCart;

    if (!comment) {
      return;
    }

    const match = getContactDataMatch();

    if (match) {
      const cleanedComment = comment.slice(0, match.index);
      shoppingCartApi.setComment(cleanedComment);
    }
  };

  // Add name and phone number at the bottom of comment
  const addContactDataToComment = () => {
    if (!config.COLLECT_CUSTOMER_CONTACT_DATA) {
      return;
    }

    const comment = shoppingCart.comment ?? '';

    // Prevent re-adding
    const match = getContactDataMatch();

    if (match) {
      return;
    }

    // Remove extra spaces and line breaks at the end of the comment
    const cleanedComment = comment.replace(/\s+$/, '');
    // If there is a comment text - indent 2 lines
    const topOffset = comment.length > 0 ? '\n\n' : '';

    const title = i18n('Cart.CustomerData.Title');
    const nameTitle = i18n('Signup.Name');
    const phoneTitle = i18n('Input.Phone');

    // Add contact data at the end of the comment
    shoppingCartApi.setComment(
      `${cleanedComment}${topOffset}${title}:\n${nameTitle}: ${contactData.name}\n${phoneTitle}: ${contactData.phone}`,
    );
  };

  const isValidContactData = () => {
    if (!config.COLLECT_CUSTOMER_CONTACT_DATA) {
      return true;
    }

    const errors = { name: '', phone: '' };
    const nameValid = /(\w\s).+/.test(contactData.name);
    const phoneNumber = parsePhoneNumberFromString(contactData.phone);
    const phoneValid = Boolean(phoneNumber?.isValid());

    if (!nameValid) {
      errors.name = i18n('SignUp.Error.Name');
    }

    if (!phoneValid) {
      errors.phone = i18n('SignUp.Error.Phone');
    }

    setContactData((prev) => ({ ...prev, errors }));

    return nameValid && phoneValid;
  };

  // Display only the customer's comment without contact data
  const commentWithoutContactData = useMemo(() => {
    const { comment } = shoppingCart;

    if (!comment) {
      return comment;
    }

    const match = getContactDataMatch();

    if (match) {
      return comment.slice(0, match.index);
    }

    return comment;
  }, [shoppingCart.comment]);

  const contactDataFormEnabled = !!config.COLLECT_CUSTOMER_CONTACT_DATA && !user;

  return {
    contactData,
    contactDataFormEnabled,
    onChangeContactData,
    isValidContactData,
    addContactDataToComment,
    removeContactDataFromComment,
    commentWithoutContactData,
  };
};

export default useCustomerData;
